import { getContext, takeEvery, call, select, take } from 'redux-saga/effects';
import { EDIT_LISTING, NEW_LISTING } from 'store/state/uploadBulletin/types';
import { RootAction } from 'store/state';
import { TRANSITION_SUCCESS } from 'store/state/router/types';
import { Route } from 'config/routes';
import { getRouteParams } from 'store/state/selectors/router';
import { SET_MUTATION_RESPONSE } from 'store/state/mutationsResponse/types';
import { LoadType, MutationType, ListingResponseType } from '../apiService/types';
import { newListingAction } from 'store/state/uploadBulletin/actions';
import { SetMutationResponseAction } from 'store/state/mutationsResponse/actions';
import { DealType, IBulletin } from 'utils/entities';
import { SET_DOMAIN_DATA } from 'store/state/domainData/types';
import { editListingSelector } from 'store/state/domainData/selectors/editListing';
import { newListingValidationResponseSelector, IMutationResponse } from 'store/state/mutationsResponse/selectors';

function* createListingWatcher(action: ReturnType<typeof newListingAction>) {
  const logger = yield getContext('logger');
  const { sendEvent } = yield getContext('analytics');
  const { source } = yield select(getRouteParams);
  const dealType = action.payload.listing.dealType;
  const eventSource = source || 'other';
  try {
    yield call(sendEvent, 'bulletin_upload_wizard_submit', 'bulletin_upload_wizard', {  event: { source: eventSource } });

    const responseAction: SetMutationResponseAction<MutationType.NewListing | MutationType.NewCommercialListing> = yield take((a: RootAction) => (
      a.type === SET_MUTATION_RESPONSE && (a.mutationType === MutationType.NewCommercialListing || a.mutationType === MutationType.NewListing)
    ));
    if (responseAction.meta.response.data) {
      const listing = 'newCommercialListing' in responseAction.meta.response.data
        ? responseAction.meta.response.data.newCommercialListing.commercialListing
        : responseAction.meta.response.data.newListing.listing;

      if (listing.id) {
        yield call(sendEvent, 'bulletin_upload_wizard_submit_success', 'bulletin_upload_wizard', {
          event: { source: eventSource },
          property: {
            deal_type: dealType === DealType.Buy ? 'for_sale' : 'for_rent',
            property_id: listing.id,
          },
        });
      }

      const listingData = 'newCommercialListing' in responseAction.meta.response.data
        ? responseAction.meta.response.data.newCommercialListing
        : responseAction.meta.response.data.newListing;

      switch (listingData.__typename) {
        case ListingResponseType.UserCannotAddListingError:
        case ListingResponseType.GeneralListingError:
        case ListingResponseType.InvalidInputError:
          yield call(sendEvent, 'bulletin_upload_wizard_submit_failed', 'bulletin_upload_wizard', {
            event: {
              source: eventSource,
              failure_stage: 'server_error',
              failure_code: String(listingData.errorMessage).slice(0, 100),
            },
            property: {
              deal_type: dealType === DealType.Buy ? 'for_sale' : 'for_rent',
            },
          });
          break;
        default:
          break;
      }

    }
    else {
      yield call(sendEvent, 'bulletin_upload_wizard_submit_failed', 'bulletin_upload_wizard', {
        event: {
          source: eventSource,
          failure_stage: 'client_validation',
        },
        property: {
          deal_type: dealType === DealType.Buy ? 'for_sale' : 'for_rent',
        },
      });
    }
  }
  catch (error) {
    yield call(sendEvent, 'bulletin_upload_wizard_submit_failed', 'bulletin_upload_wizard', {
      event: {
        source: eventSource,
        failure_stage: 'server_error',
        failure_code: String(error).slice(0, 100),
      },
      property: {
        deal_type: dealType === DealType.Buy ? 'for_sale' : 'for_rent',
      },
    });
    logger.error('Error in analytics watcher about new listing upload.', error);
  }
}

function* editListingWatcher(action: ReturnType<typeof newListingAction>) {
  const logger = yield getContext('logger');
  try {
    const { sendEvent } = yield getContext('analytics');
    const { source } = yield select(getRouteParams);
    const dealType = action.payload.listing.dealType;

    const eventSource = source || 'other';
    yield call(
      sendEvent,
      'bulletin_manage_edit_submit',
      'bulletin_manage',
      {
        event: { source: eventSource },
        property: {
          deal_type: dealType === DealType.Buy ? 'for_sale' : 'for_rent',
          property_id: action.payload.listing.id,
        },
      }
    );

    const responseAction: SetMutationResponseAction<MutationType.EditListing | MutationType.EditCommercialListing> = yield take((a: RootAction) => (
      a.type === SET_MUTATION_RESPONSE && (a.mutationType === MutationType.EditCommercialListing || a.mutationType === MutationType.EditListing)
    ));

    if (responseAction.meta.response.data) {
      const listing = 'editListing' in responseAction.meta.response.data
        ? responseAction.meta.response.data.editListing.listing
        : responseAction.meta.response.data.editCommercialListing.commercialListing;

      if (listing.id) {
        yield call(sendEvent, 'bulletin_manage_edit_submit_success', 'bulletin_manage', {
          event: { source: eventSource },
          property: {
            deal_type: dealType === DealType.Buy ? 'for_sale' : 'for_rent',
            property_id: listing.id,
          },
        });
      }
    }
  }
  catch (error) {
    logger.error('Error in analytics watcher about new listing upload.', error);
  }
}

const openUploadBulletinPagePattern = (action: RootAction) => (
  (action.type === TRANSITION_SUCCESS && action.payload.route.name === Route.UploadBulletin
    && (!action.payload.previousRoute || action.payload.previousRoute.name !== Route.UploadBulletin))
);

const openEditBulletinPagePattern = (action: RootAction) => (
  (action.type === TRANSITION_SUCCESS && action.payload.route.name === Route.EditBulletin
    && (!action.payload.previousRoute || action.payload.previousRoute.name !== Route.EditBulletin))
);

const setEditListingPattern = (action: RootAction) => (
  action.type === SET_DOMAIN_DATA && action.loadType === LoadType.EditListingPoi
);

function* openUploadBulletinPageWatcher() {
  const { sendEvent } = yield getContext('analytics');
  const { source } = yield select(getRouteParams);

  yield call(sendEvent, 'bulletin_upload_wizard_open', 'bulletin_upload_wizard', {
    event: {
      source: source || 'other',
    },
  });
}

function* openEditBulletinPageWatcher() {
  const { sendEvent } = yield getContext('analytics');
  const { source, id } = yield select(getRouteParams);

  let currentEditPoi: IBulletin = yield select(editListingSelector);

  if (!currentEditPoi || currentEditPoi.id !== id) {
    yield take(setEditListingPattern);
    currentEditPoi = yield select(editListingSelector);
  }

  yield call(sendEvent, 'bulletin_manage_edit_open', 'bulletin_manage', {
    event: {
      source: source || 'other',
    },
    property: {
      property_id: id,
      deal_type: currentEditPoi.dealType === DealType.Buy ? 'for_sale' : 'for_rent',
    },
  });
}

function* newListingValidationWatcher() {
  const { sendEvent } = yield getContext('analytics');
  const { source } = yield select(getRouteParams);
  const eventSource = source || 'other';
  const res: IMutationResponse<MutationType.NewListingValidation> = yield select(newListingValidationResponseSelector);
  const data = res.data && res.data.newListingValidation;
  switch (data.__typename) {
    case ListingResponseType.UserCannotAddListingError:
    case ListingResponseType.GeneralListingError:
    case ListingResponseType.InvalidInputError:
      yield call(sendEvent, 'bulletin_upload_wizard_submit_failed', 'bulletin_upload_wizard', {
        event: {
          source: eventSource,
          failure_stage: 'server_error',
          failure_code: String(data.errorMessage).slice(0, 100),
        },
      });
      break;
    default:
      break;
  }
}


const newListingValidationMutationPattern = (action: RootAction) => (
  action.type === SET_MUTATION_RESPONSE && action.mutationType === MutationType.NewListingValidation
);

export function* uploadBulletinEventsWorker() {
  yield takeEvery(NEW_LISTING, createListingWatcher);
  yield takeEvery(newListingValidationMutationPattern, newListingValidationWatcher);
  yield takeEvery(EDIT_LISTING, editListingWatcher);
  yield takeEvery(openUploadBulletinPagePattern, openUploadBulletinPageWatcher);
  yield takeEvery(openEditBulletinPagePattern, openEditBulletinPageWatcher);
}
